import {
  convertHeicToJpeg,
  isHeicFile as isHeicConverterFile,
  toJpegFilename,
} from '../../utils/heicConverter';

export const FUNDING_UPLOAD_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.heic,.heif,image/jpeg,image/png,image/heic,image/heif,image/heic-sequence,image/heif-sequence,application/pdf';

export const FUNDING_UPLOAD_DROPZONE_LABEL = 'Drop PDF, PNG, JPG, JPEG, HEIC, or HEIF files here';

export const FUNDING_ALLOWED_EXTENSIONS = new Set([
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.heic',
  '.heif',
]);

export const FUNDING_ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

export const HEIC_CONVERSION_ERROR =
  'This HEIC photo could not be converted. Please upload it as JPG, PNG, or PDF.';

export const UNSUPPORTED_FUNDING_UPLOAD_MESSAGE =
  'Unsupported file type. Upload PDF, PNG, JPG, JPEG, HEIC, or HEIF.';

function getExtension(fileName = '') {
  const match = String(fileName).toLowerCase().match(/(\.[^.]+)$/);
  return match ? match[1] : '';
}

export function isHeicFile(file) {
  if (!file) return false;
  if (isHeicConverterFile(file)) return true;
  const mime = String(file.type || '').toLowerCase();
  if (mime.includes('heic') || mime.includes('heif')) {
    return true;
  }
  const ext = getExtension(file.name);
  return ext === '.heic' || ext === '.heif';
}

export function isSupportedFundingUploadFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime && FUNDING_ALLOWED_MIME_TYPES.has(mime)) return true;
  return FUNDING_ALLOWED_EXTENSIONS.has(getExtension(file.name));
}

function revokePreviewUrl(url) {
  if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
}

/**
 * Normalize a single upload for FundGenius:
 * - validate type
 * - convert HEIC/HEIF → JPEG
 * - attach conversion metadata for preview/OCR/save
 */
export async function normalizeFundingUploadFile(file) {
  if (!isSupportedFundingUploadFile(file)) {
    const error = new Error(UNSUPPORTED_FUNDING_UPLOAD_MESSAGE);
    error.code = 'unsupported_file';
    throw error;
  }

  if (!isHeicFile(file)) {
    return {
      file,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size,
      previewUrl: null,
      originalFilename: file.name,
      convertedFilename: null,
      originalMimeType: file.type || 'application/octet-stream',
      convertedFromHeic: false,
      conversionStatus: 'ready',
      conversionError: null,
    };
  }

  try {
    const converted = await convertHeicToJpeg(file);
    const convertedFilename = converted.originalName || toJpegFilename(file.name);
    const jpegFile = new File([converted.blob], convertedFilename, {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });

    return {
      file: jpegFile,
      fileName: convertedFilename,
      mimeType: 'image/jpeg',
      fileSize: jpegFile.size,
      previewUrl: converted.url,
      originalFilename: file.name,
      convertedFilename,
      originalMimeType: file.type || 'image/heic',
      convertedFromHeic: true,
      conversionStatus: 'converted',
      conversionError: null,
    };
  } catch (error) {
    const conversionError = new Error(HEIC_CONVERSION_ERROR);
    conversionError.code = 'heic_conversion_failed';
    conversionError.cause = error;
    throw conversionError;
  }
}

export async function normalizeFundingUploadFiles(files = []) {
  const list = Array.from(files || []);
  const accepted = [];
  const rejected = [];

  for (const file of list) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const normalized = await normalizeFundingUploadFile(file);
      accepted.push(normalized);
    } catch (error) {
      rejected.push({
        fileName: file?.name || 'upload',
        message: error?.message || UNSUPPORTED_FUNDING_UPLOAD_MESSAGE,
        code: error?.code || 'upload_failed',
      });
    }
  }

  return { accepted, rejected };
}

export function cleanupFundingPreviewUrl(doc) {
  revokePreviewUrl(doc?.previewUrl);
}

export function cleanupFundingPreviewUrls(documents = []) {
  documents.forEach(cleanupFundingPreviewUrl);
}
